/**
 * Notification Deduplication Utility
 *
 * Prevents notification spam by grouping rapid duplicate notifications.
 * For example, if a user shares 5 files rapidly:
 * - First share: Shows "Shared file with caller"
 * - Shares 2-5: Suppressed (within window)
 * - After window: Shows "Shared 5 files with caller" (grouped)
 */

import { DedupeResult, DEFAULT_DEDUPE_WINDOW_MS } from './types';

interface DedupeEntry {
  count: number;
  lastSeen: number;
  firstSeen: number;
}

/**
 * Notification deduplicator using sliding time windows.
 * Tracks recent notification keys and prevents duplicates within the window.
 */
class NotificationDeduplicator {
  private recentKeys = new Map<string, DedupeEntry>();
  private cleanupInterval: ReturnType<typeof setInterval> | null = null;
  private readonly maxEntries = 1000; // Prevent memory leaks

  constructor() {
    // Run cleanup every minute
    if (typeof window !== 'undefined') {
      this.cleanupInterval = setInterval(() => this.cleanup(), 60000);
    }
  }

  /**
   * Check if a notification should be shown or grouped.
   *
   * @param dedupeKey - Unique key for the notification type (e.g., 'file.shared:123')
   * @param windowMs - Time window for deduplication in milliseconds
   * @returns DedupeResult indicating whether to show and group count
   */
  check(dedupeKey: string, windowMs: number = DEFAULT_DEDUPE_WINDOW_MS): DedupeResult {
    const now = Date.now();
    const existing = this.recentKeys.get(dedupeKey);

    if (existing && (now - existing.lastSeen) < windowMs) {
      // Within window - increment count, don't show individual notification
      existing.count++;
      existing.lastSeen = now;
      return { show: false, groupCount: existing.count };
    }

    // Check if we had a previous group that should be summarized
    if (existing && existing.count > 1) {
      // Previous group exists - show grouped summary, then reset
      const groupCount = existing.count;
      this.recentKeys.set(dedupeKey, { count: 1, lastSeen: now, firstSeen: now });
      return { show: true, groupCount, isGrouped: true };
    }

    // Outside window or new - reset and show
    this.recentKeys.set(dedupeKey, { count: 1, lastSeen: now, firstSeen: now });

    // Prevent memory leaks
    if (this.recentKeys.size > this.maxEntries) {
      this.cleanup();
    }

    return { show: true };
  }

  /**
   * Get pending group count for a key without triggering a check.
   * Useful for checking if there are pending grouped notifications.
   */
  getPendingCount(dedupeKey: string): number {
    const entry = this.recentKeys.get(dedupeKey);
    return entry?.count || 0;
  }

  /**
   * Force a grouped notification to be shown now.
   * Useful when a user action requires immediate feedback.
   *
   * @param dedupeKey - The key to flush
   * @returns The count of grouped items, or 0 if none
   */
  flush(dedupeKey: string): number {
    const entry = this.recentKeys.get(dedupeKey);
    if (entry && entry.count > 1) {
      const count = entry.count;
      this.recentKeys.delete(dedupeKey);
      return count;
    }
    this.recentKeys.delete(dedupeKey);
    return 0;
  }

  /**
   * Clear a specific key from tracking.
   */
  clear(dedupeKey: string): void {
    this.recentKeys.delete(dedupeKey);
  }

  /**
   * Clear all tracked keys.
   */
  clearAll(): void {
    this.recentKeys.clear();
  }

  /**
   * Remove expired entries to prevent memory leaks.
   */
  private cleanup(): void {
    const now = Date.now();
    const maxAge = 600000; // 10 minutes max age

    for (const [key, entry] of this.recentKeys.entries()) {
      if (now - entry.lastSeen > maxAge) {
        this.recentKeys.delete(key);
      }
    }
  }

  /**
   * Destroy the deduplicator and clean up resources.
   */
  destroy(): void {
    if (this.cleanupInterval) {
      clearInterval(this.cleanupInterval);
      this.cleanupInterval = null;
    }
    this.recentKeys.clear();
  }
}

// Singleton instance
let deduplicatorInstance: NotificationDeduplicator | null = null;

/**
 * Get the singleton deduplicator instance.
 */
export function getDeduplicator(): NotificationDeduplicator {
  if (!deduplicatorInstance) {
    deduplicatorInstance = new NotificationDeduplicator();
  }
  return deduplicatorInstance;
}

/**
 * Check if a notification should be deduplicated.
 *
 * @param dedupeKey - Unique key for the notification type
 * @param windowMs - Time window for deduplication
 * @returns DedupeResult indicating whether to show
 */
export function checkDedupe(
  dedupeKey: string,
  windowMs: number = DEFAULT_DEDUPE_WINDOW_MS
): DedupeResult {
  return getDeduplicator().check(dedupeKey, windowMs);
}

/**
 * Flush a pending grouped notification.
 *
 * @param dedupeKey - The key to flush
 * @returns The count of grouped items
 */
export function flushDedupe(dedupeKey: string): number {
  return getDeduplicator().flush(dedupeKey);
}

/**
 * Clear deduplication tracking for a key.
 */
export function clearDedupe(dedupeKey: string): void {
  getDeduplicator().clear(dedupeKey);
}

// Export the class for testing
export { NotificationDeduplicator };
